import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import type { FormEvent } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { AuthShell } from './AuthShell'
import { Alert, Button, mutedTextClass } from '@/components/layout/ui'
import { authErrorMessage, authLinkClass, FormError, PasswordField } from '@/features/auth'
import { sessionApi } from '@/lib/api'
import { cn } from '@/lib/cn'
import { fieldErrorFor } from '@/lib/errors'

// Only the fields this form actually shows. An error on `token` or `email` —
// the expired-link case — has no input to sit under, so it is left to the
// form-level region, where it reads as what it is: the link, not the typing.
const VISIBLE_FIELDS = ['password', 'password_confirmation']

/**
 * Finish a password reset.
 *
 * The token and address arrive in the link's query string. The address is shown
 * rather than asked for: it is half of the credential the token was issued
 * against, so an editable field could only ever be used to get it wrong, and
 * seeing which account is being changed is the check that actually matters.
 *
 * A completed reset revokes every browser session but deliberately leaves
 * bearer tokens alone, so an iPad mid-sync does not lose its offline queue —
 * which is why this page sends the user back to sign in rather than assuming
 * they are still signed in.
 */
export default function ResetPasswordPage() {
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  const email = (params.get('email') ?? '').trim()
  const [password, setPassword] = useState('')
  const [confirmation, setConfirmation] = useState('')

  const reset = useMutation({
    mutationFn: () =>
      sessionApi.resetPassword({ token, email, password, password_confirmation: confirmation }),
  })

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    reset.mutate()
  }

  // The server attaches an unusable link to `token` or `email`; either way the
  // only move left is to ask for a fresh one.
  const linkIsDead = Boolean(
    fieldErrorFor(reset.error, 'token') ?? fieldErrorFor(reset.error, 'email'),
  )

  if (!token || !email) {
    return (
      <AuthShell
        title="This link is incomplete"
        description={token ? 'It is missing the address it was issued for.' : 'It is missing its reset token.'}
      >
        <Alert tone="warning">
          Open the most recent link from your email, or{' '}
          <Link to="/forgot-password" className="font-medium underline">
            request a new one
          </Link>
          .
        </Alert>
      </AuthShell>
    )
  }

  return (
    <AuthShell
      title="Choose a new password"
      footer={
        <Link to="/login" className={authLinkClass}>
          Back to log in
        </Link>
      }
    >
      {reset.isSuccess ? (
        <div className="space-y-5">
          <Alert tone="success" title="Password updated">
            {reset.data.message} You can log in with it now.
          </Alert>
          <Link
            to="/login"
            className="flex h-10 w-full items-center justify-center rounded-lg bg-brand-600 text-sm font-medium text-white transition hover:bg-brand-700"
          >
            Log in
          </Link>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5" noValidate>
          <div>
            <p className={cn('text-sm', mutedTextClass)}>Setting a new password for</p>
            <p className="truncate text-sm font-medium" title={email}>
              {email}
            </p>
          </div>

          {/* Present for the password manager, which needs the account this
              new password belongs to; the line above is what people read. */}
          <input
            type="text"
            name="email"
            value={email}
            autoComplete="username"
            readOnly
            tabIndex={-1}
            aria-hidden="true"
            className="sr-only"
          />

          <PasswordField
            label="New password"
            name="password"
            autoComplete="new-password"
            required
            minLength={8}
            autoFocus
            hint="At least 8 characters."
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            error={fieldErrorFor(reset.error, 'password')}
          />
          <PasswordField
            label="Confirm new password"
            name="password_confirmation"
            autoComplete="new-password"
            required
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
            error={fieldErrorFor(reset.error, 'password_confirmation')}
          />

          <FormError message={authErrorMessage(reset.error, VISIBLE_FIELDS, 'reset attempts')} />
          {linkIsDead ? (
            <p className="text-sm">
              <Link to="/forgot-password" className={authLinkClass}>
                Request a new reset link
              </Link>
            </p>
          ) : null}

          <Button type="submit" variant="primary" className="w-full" loading={reset.isPending}>
            {reset.isPending ? 'Updating…' : 'Update password'}
          </Button>
        </form>
      )}
    </AuthShell>
  )
}
